<template>
  <q-card v-if="isOpen" class="map-panel">
    <div class="panel-content q-pa-md">
    <!-- Data Layers -->
    <div class="q-mb-md">
      <div class="text-subtitle2 section-title q-mb-sm">{{ $t('Data Layers') }}</div>
      <q-select
        v-model="selectedDataLayer"
        :options="dataLayers"
        option-value="id"
        option-label="name"
        emit-value
        outlined
        dense
        map-options
        behavior="menu"
        :color="$q.dark.isActive ? 'secondary' : 'primary'"
        @update:model-value="handleDataLayerChange"
      >
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section>
              <div v-html="scope.opt.displayName || scope.opt.name"></div>
            </q-item-section>
          </q-item>
        </template>

        <template v-slot:selected-item="scope">
          <div v-html="scope.opt.displayName || scope.opt.name || $t('Select layer')"></div>
        </template>
      </q-select>
    </div>

    <q-separator class="q-my-md" />

    <!-- Base Layers -->
    <div class="q-mb-md">
      <div class="text-subtitle2 section-title q-mb-sm">{{ $t('Base Layers') }}</div>
      <q-select
        v-model="selectedBase"
        :options="baseLayers"
        option-value="id"
        option-label="name"
        emit-value
        map-options
        outlined
        dense
        behavior="menu"
        :color="$q.dark.isActive ? 'secondary' : 'primary'"
        @update:model-value="handleBaseChange"
      />
    </div>

    <q-separator class="q-my-md" />

    <!-- Opacity Slider -->
    <div>
      <div class="text-subtitle2 section-title q-mb-sm">
        {{ $t('Opacity') }}: {{ Math.round(opacity * 100) }}%
      </div>
      <q-slider
        v-model="opacity"
        :min="0"
        :max="1"
        :step="0.01"
        :color="$q.dark.isActive ? 'secondary' : 'primary'"
        :track-color="$q.dark.isActive ? 'grey-8' : undefined"
        @update:model-value="handleOpacityChange"
      />
    </div>

    <q-separator class="q-my-md" />

    <!-- Advanced Settings -->
    <div>
      <div class="settings-header q-mb-sm" @click="settingsExpanded = !settingsExpanded">
        <div class="text-subtitle2 section-title">{{ $t('Advanced') }}</div>
        <q-icon name="tune" size="18px" />
      </div>

      <q-slide-transition>
        <div v-show="settingsExpanded" class="q-pt-sm">
          <!-- Projection -->
          <div class="setting-row q-mb-sm">
            <span class="setting-label">{{ $t('Projection') }}</span>
            <q-btn-toggle
              v-model="selectedProjection"
              :toggle-color="$q.dark.isActive ? 'secondary' : 'primary'"
              :options="[
                { value: 'EPSG:3035', slot: 'laea' },
                { value: 'EPSG:3857', slot: 'web' }
              ]"
              size="md"
              dense
              unelevated
              @update:model-value="handleProjectionChange"
            >
              <template v-slot:laea>
                <q-icon name="public" size="16px">
                  <q-tooltip>LAEA Europe</q-tooltip>
                </q-icon>
              </template>
              <template v-slot:web>
                <q-icon name="map" size="16px">
                  <q-tooltip>Web Mercator</q-tooltip>
                </q-icon>
              </template>
            </q-btn-toggle>
          </div>

          <!-- Field Boundaries -->
          <div class="setting-row">
            <span class="setting-label">{{ $t('Show fields') }}</span>
            <q-toggle
              v-model="showFieldBoundaries"
              dense
              size="xs"
              :color="$q.dark.isActive ? 'secondary' : 'primary'"
              @update:model-value="handleFieldBoundariesToggle"
            />
          </div>
        </div>
      </q-slide-transition>
    </div>
    </div>
  </q-card>
</template>

<script>
export default {
  name: 'LayerMenu',
  
  props: {
    isOpen: Boolean,
    selectedLayerId: [String, Number],
    selectedBaseLayer: String,
    layerOpacity: Number,
    dataLayers: Array,
    baseLayers: Array,
    currentProjection: { type: String, default: 'EPSG:3035' },
    fieldBoundariesVisible: { type: Boolean, default: true }
  },

  emits: ['layer-changed', 'base-layer-changed', 'opacity-changed', 'projection-changed', 'field-boundaries-toggled', 'close'],

  data() {
    return {        
      selectedDataLayer: this.selectedLayerId,
      selectedBase: this.selectedBaseLayer,
      opacity: this.layerOpacity,
      settingsExpanded: false,
      selectedProjection: this.currentProjection,
      showFieldBoundaries: this.fieldBoundariesVisible
    }
  },

  watch: {
    selectedLayerId(v) { this.selectedDataLayer = v },
    selectedBaseLayer(v) { this.selectedBase = v },
    layerOpacity(v) { this.opacity = v },
    currentProjection(v) { this.selectedProjection = v },
    fieldBoundariesVisible(v) { this.showFieldBoundaries = v },
    isOpen(val) {
      val 
        ? setTimeout(() => document.addEventListener('click', this.onClickOutside), 50)
        : document.removeEventListener('click', this.onClickOutside)
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside)
  },

  methods: {
    onClickOutside(e) {
      if (!this.$el?.contains(e.target)) {
        this.$emit('close')
      }
    },
    handleDataLayerChange(layerId) {
      this.$emit('layer-changed', parseInt(layerId))
    },
    handleBaseChange(layerId) {
      this.$emit('base-layer-changed', layerId)
    },
    handleOpacityChange(value) {
      this.$emit('opacity-changed', value)
    },
    handleProjectionChange(projection) {
      this.$emit('projection-changed', projection)
    },
    handleFieldBoundariesToggle(value) {
      this.$emit('field-boundaries-toggled', value)
    }
  }
}
</script>